/**
 * Phone-number normalization + display formatting.
 *
 * Storage + dial rule: every phone handed to the softphone is E.164.
 * Formatting for display is a separate concern handled by `formatPhone`.
 */

/** Strip everything except digits. */
export function digitsOnly(s: string): string {
  return s.replace(/\D+/g, '');
}

/**
 * Normalize any phone-ish input (raw paste, display-formatted, bare
 * digits, E.164) to strict E.164 (`+1XXXXXXXXXX` for US). Returns the
 * empty string when the input isn't long enough to be a dialable number.
 *
 * Rules:
 *   - 10 digits → assume US, prefix `+1`
 *   - 11 digits starting with `1` → add `+`
 *   - 12+ digits → treat as international, add `+`
 *   - Fewer than 10 digits → not yet dialable → ""
 */
export function toE164(raw: string): string {
  if (!raw) return '';
  const d = digitsOnly(raw);
  if (d.length === 10) return `+1${d}`;
  if (d.length === 11 && d.startsWith('1')) return `+${d}`;
  if (d.length >= 12) return `+${d}`;
  return '';
}

/**
 * Format a phone value for human-readable display as `(502) 744-6660`.
 * Accepts any of: E.164 (`+15027446660`), bare 11-digit (`15027446660`),
 * bare 10-digit (`5027446660`), or anything partial while typing.
 * Non-US-shaped longer values pass through with a leading `+`.
 */
export function formatPhone(value: string): string {
  if (!value) return '';
  const d = digitsOnly(value);
  if (d.length > 11) return `+${d}`;
  const ten = d.length === 11 && d.startsWith('1') ? d.slice(1) : d;
  if (ten.length === 0) return '';
  if (ten.length <= 3) return `(${ten}`;
  if (ten.length <= 6) return `(${ten.slice(0, 3)}) ${ten.slice(3)}`;
  return `(${ten.slice(0, 3)}) ${ten.slice(3, 6)}-${ten.slice(6, 10)}`;
}
